/**
 * Стартовий екран «/» (UX-01). Після входу стартову сторінку визначає `/meta` (`start`, views.md,
 * розд. 5) чи особиста стартова з профілю: «/» переадресовує туди, а нема жодного доступного
 * розділу — стан «Немає доступних розділів». Без входу — сторінка входу (демо S4 без входу
 * прибрано, тікет 8e4d4110).
 */
import type { QueryClient } from '@tanstack/react-query';
import { createRoute, redirect } from '@tanstack/react-router';
import { usePageTitle } from '../../brand/env.ts';
import { hasAdmin } from '../../features/admin/registry.ts';
import { prefsText as p } from '../../i18n/uk/prefs.ts';
import { shell as t } from '../../i18n/uk/shell.ts';
import { states as st } from '../../i18n/uk/states.ts';
import { hasSessionHint, isUnauthorized, platformMetaQuery } from '../../platform/meta.ts';
import { navItems } from '../../platform/modules.ts';
import { getPrefs } from '../../prefs/prefs.ts';
import { startPrefsSync } from '../../prefs/sync.ts';
import { buildNav, withPersonalStart } from '../../shell/nav-model.ts';
import { toast } from '../../ui/notify/store.ts';
import { SkeletonTable } from '../../ui/Skeleton.tsx';
import { StateView } from '../../ui/StateView.tsx';
import { LOGIN_PATH, rootRoute } from './root.tsx';

/**
 * Стартовий маршрут: особиста стартова з профілю (PRF-06), якщо вона доступна, інакше — за `/meta`;
 * `undefined` — входу немає (сесія скінчилась). Налаштування профілю й метадані — паралельно, одним
 * очікуванням: без них «/» відкрив би стартову компанії, а за мить — іншу.
 */
async function platformStart(context: { queryClient: QueryClient }) {
  if (!hasSessionHint()) return undefined;
  try {
    const [meta] = await Promise.all([
      context.queryClient.ensureQueryData(platformMetaQuery()),
      startPrefsSync(),
    ]);
    const nav = buildNav(meta, navItems(meta), hasAdmin(meta) ? '' : null);
    const personal = withPersonalStart(nav, getPrefs().startPage);
    if (personal.rejected) toast({ text: p.startFallback, key: 'start-fallback' });
    return personal.nav.start;
  } catch (error) {
    if (isUnauthorized(error)) return undefined;
    throw error;
  }
}

/**
 * Поки `/meta` і налаштування ще в дорозі (довше 300 мс): панель і скелет списку, куди найчастіше
 * веде «/». Назву дасть скелетон самої сторінки після переадресації.
 */
function HomeSkeleton() {
  return (
    <>
      <div className="toolbar" />
      <div className="content" aria-busy="true">
        <SkeletonTable label={st.loading} rows={12} />
      </div>
    </>
  );
}

function NoSections() {
  usePageTitle(t.noSectionsTitle);
  return <StateView icon="lock" title={t.noSectionsTitle} text={t.noSectionsText} />;
}

export const homeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  loader: async ({ context }) => {
    const start = await platformStart(context);
    if (start) throw redirect({ href: start, replace: true });
    if (start === null) return;
    throw redirect({ to: LOGIN_PATH, replace: true });
  },
  pendingComponent: HomeSkeleton,
  component: NoSections,
});
